import React, { useContext, useEffect, useState } from 'react';
import '@cm/design/styles.css';
import { labelByKey } from '@cm/new-shared-fe/services/labels';
import { getParameter } from '@cm/new-shared-fe/services/parameters';
import { Button, Fieldset, InfoField, SvgDefs } from '@cm/react-components';

import { AuthContext } from './auth.context.js';
import { InputField } from './components/input-field.component';
import { MultiSelectDropdown, OTHER_OPTION } from './components/multi-select.component';
import { LoginMyCm } from './components/login.component.jsx';
import { sendEzvkRequest } from './services/ezvk-service';
import { findFamilyMembers, findMemberNameForNrn } from './services/member-service';

export const Application = () => {
  const auth = useContext(AuthContext);

  const [familyMembers, setFamilyMembers] = useState(null);
  const [value, setValue] = useState('');
  const [selected, setSelected] = useState([]);
  const [showNRNField, setShowNRNField] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [showInputRequiredError, setInputRequiredError] = useState(false);

  const multiSelectRef = React.createRef();
  const inputRef = React.createRef();

  useEffect(async () => {
    if (auth.isLoggedIn) {
      try {
        const familyMembers = await findFamilyMembers();
        if (familyMembers === null) {
          setShowNRNField(true);
          console.info('Family composition can not be determined. Not showing multiselect');
          // Not secure user (either not logged on or light user), but no error.
        }
        setFamilyMembers(familyMembers);
      } catch (error) {
        setShowNRNField(true);
        console.log(error);
      }
    }
  }, [auth]);

  /** @type {(values: string[]) => void} */
  const handleValuesChange = (values) => {
    const otherSelected = values.includes(OTHER_OPTION);

    setShowNRNField(otherSelected);
    setSelected(values.filter(value => value !== OTHER_OPTION));

    if (otherSelected && multiSelectRef.current) {
      multiSelectRef.current.close();
    }
    if (!otherSelected) {
      setValue('');
    }
  };

  const resetFields = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    if (multiSelectRef.current) {
      multiSelectRef.current.clearValue();
    }
    setLoading(false);
    setShowNRNField(false);
  };

  /**
   * @type {(e: Event) => void }
   */
  const handleSubmit = (e) => {
    e.preventDefault();

    // Clear results if any
    setResults([]);

    if (showNRNField && !value) {
      setInputRequiredError(true);
      return;
    }

    if (!value && selected.length === 0) return;

    setLoading(true);
    sendEzvkRequest(selected, value)
      .then((data) => {
        setResults(data.info);
      })
      .catch((error) => {
        setResults(error);
      })
      .finally(() => {
        resetFields();
      });
  };

  const renderMessages = () => {
    if (results instanceof Error) {
      return <InfoField className="mt-1.5" error="true">{labelByKey(`ezvk.request.${results.code}`)}</InfoField>;
    }

    return results.map((result, i) => {
      const memberName = findMemberNameForNrn(familyMembers, result.nrn);
      if (result.success) {
        return (
          <div key={i}>
            <InfoField className="mt-1.5" success="true">{labelByKey(result.hidden ? 'ezvk.request.success.hidden' : 'ezvk.request.success', { memberName })}</InfoField>
            {result.incapacitated && <InfoField icon="form-information">{labelByKey('ezvk.request.incapacitated', { memberName })}</InfoField>}
          </div>
        );
      }
      return <InfoField className="mt-1.5" error="true" key={i}>{labelByKey(`ezvk.request.${result.reason}`, { memberName })}</InfoField>;
    });
  };

  return (
    <div className="container max-w-screen-lg mx-auto px-4">
      {
        /**
         * This reset-css-like is needed to prevent Olga's styles from Tridion (styles.min.css)
         * to infect the <ezvk-app>
         * It is also solved by making the shadowDom closed, but then it's not possible anymore
         * to write E2E tests (can not select into the closed shadow dom for elements
         * So we were forced to open the shadowDom. To counter the CSS injections, the following
         * reset works but this is far from elegant.
         *
         * We should talk with Olga about this to find a solution
         *    a) put the reset inside Tailwind
         *    b) have olga make a not(.tw-app) and we can mark our app with <div class=tw-app>
         *    c) ???
         */
      }
      <style>
        {'\
          <!-- reset sdl -->\
          a, address, article, aside, b, big, blockquote, body, canvas, caption, center, cite, code, dd, del, details, div, dl, dt, em, fieldset, figcaption, figure, footer, form, h1, h2, h3, h4, h5, h6, header, hgroup, html, i, img, label, legend, li, mark, menu, nav, object, ol, output, p, pre, s, samp, section, small, span, strike, strong, sub, summary, sup, table, tbody, td, tfoot, th, thead, time, tr, tt, u, ul {\
            margin: unset;\
            padding: unset;\
            border: initial;\
            font-style: normal;\
            font-variant: normal;\
            font-weight: normal;\
            font-stretch: normal;\
            font-size: medium;\
            line-height: normal;\
            font-family: Open Sans, ui-sans-serif, system-ui, sans-serif;\
            vertical-align: baseline;\
          }\
          code, pre {\
            border-radius: unset;\
          }\
          .divider {\
            margin: unset;\
            color: unset;\
            width: unset;\
          }\
          .divider:after {\
            content: unset\
          }\
          .collapsing, .dropdown, .dropup {\
            position: relative;\
          }\
          <!-- end reset sdl -->\
        '}
      </style>

      <SvgDefs/>

      <Fieldset legend={labelByKey('frameset.title')}>
        {/* Design http://cm.gitlab-pages.cm.be/design/assets/html/ezvk/ezvk-not-login.html */}
        {!auth.isLoggedIn && <LoginMyCm loginUrl={getParameter('login_url')}/>}

        {auth.isLoggedIn && (
          <form className="form mb-3" onSubmit={handleSubmit}>
            {familyMembers && (
              <MultiSelectDropdown
                multiRef={multiSelectRef}
                familyMembers={familyMembers}
                onValuesChange={handleValuesChange}
              />
            )}
            {showNRNField && (
              <InputField
                className="mt-1.5"
                label={labelByKey('rnr.field.placeholder')}
                showRequiredError={showInputRequiredError}
                inputRef={inputRef}
                onChange={setValue}
              />
            )}
            {renderMessages()}
            <Button className="mt-1.5" disabled={loading} loading={loading} label={labelByKey('request.button')} data-e2e="send-request-button"/>
          </form>
        )}
      </Fieldset>
    </div>
  );
};
